import { css, ElementStyles } from '@microsoft/fast-element';
import { zeroButtonStyles as buttonStyles } from '@genesislcap/foundation-zero';
import {
  ElementDefinitionContext,
  FoundationElementDefinition,
  PropertyStyleSheetBehavior,
} from '@microsoft/fast-foundation';
import { boxSizing } from '../../../styles';

const primaryStyles = css`
  :host([appearance='primary']) {
    --design-unit: 3.6;

    border-radius: 4px;
    border: 1px solid transparent;
    color: var(--white);
    font-weight: 500;
    font-size: 13px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }

  :host([appearance='primary'])::part(control) {
    background: linear-gradient(90deg, #5e8d4c 0%, #167a58 100%);
    border: 1px solid transparent;
  }

  :host([appearance='primary']:hover) {
    color: var(--black);
  }

  :host([appearance='primary']:hover)::part(control) {
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
`;

const accentStyles = css`
  :host([appearance='accent']) {
    --design-unit: 3.6;

    border-radius: 4px;
    border: 1px solid transparent;
    color: var(--white);
    font-weight: 500;
    font-size: 13px;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }

  :host([appearance='accent'])::part(control) {
    background: linear-gradient(90deg, #5e8d4c 0%, #167a58 100%);
    border: 1px solid transparent;
  }

  :host([appearance='accent']:hover) {
    color: var(--black);
  }

  :host([appearance='accent']:hover)::part(control) {
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  }
`;

const secondaryStyles = css`
  :host([appearance='secondary']) {
    height: 36px;
    border-radius: 4px;
    border: 1px solid #7c909b;
    background-color: rgb(124 144 155 / 50%);
    font-weight: 500;
    color: var(--white);
    font-size: 13px;
  }

  :host([appearance='secondary']:hover) {
    background-color: rgb(124 144 155 / 100%);
  }
`;

const defaultStyles = css`
  :host([appearance='default']) {
    border-radius: 4px;
    border: 1px solid var(--gray-lighter);
    background-color: var(--gray);
    font-weight: 500;
    color: var(--white);
    font-size: 13px;
  }

  :host([appearance='default']:hover) {
    background-color: var(--gray-lighter);
  }
`;

const stealthStyles = css`
  :host([appearance='stealth']) {
    border-radius: 4px;
    border: 0;
    background-color: var(--gray-dark);
    font-weight: 500;
    color: var(--gray-lighter);
    font-size: 11px;
  }

  :host([appearance='stealth']:hover) {
    color: var(--white);
  }
`;

const dangerStyles = css`
  :host([appearance='danger']) {
    border: 1px solid var(--error);
    background: var(--red);
    border-radius: 4px;

    --design-unit: 3.6;
  }

  :host([appearance='danger']:hover) {
    background: var(--error);
  }
`;

const lightweightStyles = css`
  :host([appearance='lightweight']) {
    border: 0;
    color: inherit;
    width: auto;
    min-width: auto;
    height: auto;
  }

  :host([appearance='lightweight']:hover) {
    text-decoration: none;
    color: var(--primary-color);
  }

  :host([appearance='lightweight']:hover)::part(content)::before {
    display: none;
  }

  :host([appearance='lightweight']) ::slotted(svg) {
    height: 12px;
  }
`;

const tabPrimaryStyles = css`
  :host([appearance='tab-primary']) {
    height: 30px;
    color: var(--primary-color);
    background-color: rgb(255 255 255 / 5%);
  }

  :host([appearance='tab-primary'])::part(start) {
    color: var(--gray-lighter);
  }

  :host([appearance='tab-primary']:hover) {
    background-color: rgb(255 255 255 / 10%);
    color: var(--white);
  }
`;

const tabSecondaryStyles = css`
  :host([appearance='tab-secondary']) {
    height: 30px;
    color: var(--font-color-1);
    background-color: rgb(255 255 255 / 5%);
  }

  :host([appearance='tab-secondary']:hover) {
    background-color: rgb(255 255 255 / 10%);
    color: var(--white);
  }
`;

const cardPrimaryStyles = css`
  :host([appearance='card-primary']) {
    height: 32px;
    font-size: 13px;
    color: var(--primary-color);
    border-radius: 4px;
    background-color: rgb(135 155 166 / 13%);
    margin: 0;
  }

  :host([appearance='card-primary']:hover) {
    color: var(--white);
  }
`;

const cardSecondaryStyles = css`
  :host([appearance='card-secondary']) {
    height: 32px;
    border-radius: 4px;
    background-color: rgb(135 155 166 / 13%);
    font-size: 13px;
    color: var(--white-smoke);
    margin: 0;
  }
`;

const tertiaryStyles = css`
  :host([appearance='tertiary']) {
    height: 36px;
    border-radius: 4px;
    border: 1px solid gradient(-112deg, rgb(236 73 15 / 50%), rgb(254 108 150 / 50%));
    background-image: linear-gradient(-112deg, rgb(236 73 15 / 50%), rgb(254 108 150 / 50%) 100%);
    color: var(--white);
    font-weight: 500;
    font-size: 13px;
  }

  :host([appearance='tertiary']:hover) {
    background-image: linear-gradient(
      -112deg,
      rgb(236 73 15 / 100%) 0%,
      rgb(254 108 150 / 100%) 100%
    );
  }
`;

const chatCounterStyles = css`
  :host([appearance='chat-counter']) {
    background: linear-gradient(90deg, rgb(116 230 74 / 50%) 0%, rgb(205 172 31 / 51%) 100%);
    border: 1px solid var(--primary-color);
    margin: 0;
    height: 27px;
    font-family: Roboto-Regular, Roboto, sans-serif;
  }

  :host([appearance='chat-counter'][disabled]:hover) {
    background: linear-gradient(90deg, rgb(116 230 74 / 50%) 0%, rgb(205 172 31 / 51%) 100%);
  }

  :host([appearance='chat-counter']:not([disabled]):hover) {
    background: linear-gradient(90deg, #74e64a 0%, #cdac1f 100%);
    color: var(--black);
  }
`;

const chatAcceptStyles = css`
  :host([appearance='chat-accept']) {
    border: 1px solid rgb(122 204 121 / 60%);
    background-color: rgb(122 204 121 / 8%);
    color: var(--completed);
    margin: 0;
    height: 27px;
    font-family: Roboto-Regular, Roboto, sans-serif;
  }

  :host([appearance='chat-accept'])::part(content) {
    margin-bottom: 2px;
  }

  :host([appearance='chat-accept'][disabled]:hover) {
    background-color: rgb(122 204 121 / 8%);
  }

  :host([appearance='chat-accept']:not([disabled]):hover) {
    background: rgb(51 204 255 / 20%);
  }
`;

const chatRejectStyles = css`
  :host([appearance='chat-reject']) {
    border: 1px solid rgb(242 35 100 / 60%);
    background-color: rgb(242 35 100 / 8%);
    color: #f22364;
    margin: 0;
    height: 27px;
    font-family: Roboto-Regular, Roboto, sans-serif;
  }

  :host([appearance='chat-reject'])::part(content) {
    margin-bottom: 2px;
  }

  :host([appearance='chat-reject'][disabled]:hover) {
    background-color: rgb(242 35 100 / 8%);
  }

  :host([appearance='chat-reject']:not([disabled]):hover) {
    background: rgb(51 204 255 / 20%);
  }
`;

const sideBarStyles = css`
  :host([appearance='side-bar']) {
    justify-content: center;
    background: none;
    min-width: 36px;
    height: 36px;
    color: var(--gray-lighter);
  }

  :host([appearance='side-bar']:hover) {
    background-color: rgb(255 255 255 / 5%);
    border-radius: 4px;
    color: var(--white);
  }

  :host([appearance='side-bar']) ::slotted(svg) {
    width: 20px;
    height: 20px;
  }
`;

const primaryToastStyles = css`
  :host([appearance='primary-toast']) {
    background: linear-gradient(90deg, rgb(116 230 74 / 50%) 0%, rgb(205 172 31 / 51%) 100%);
    border: 1px solid #a9ff78;
    font-size: 13px;
    margin: 0;
    margin-right: 12px;
    height: 27px;
    font-family: Roboto-Medium, Roboto, sans-serif;
  }

  :host([appearance='primary-toast'][disabled]:hover) {
    background: linear-gradient(90deg, rgb(116 230 74 / 50%) 0%, rgb(205 172 31 / 51%) 100%);
  }

  :host([appearance='primary-toast']:not([disabled]):hover) {
    background: linear-gradient(90deg, #74e64a 0%, #cdac1f 100%);
    color: var(--black);
  }
`;

const secondaryToastStyles = css`
  :host([appearance='secondary-toast']) {
    background: rgba(135, 155, 166, 0.5);
    border: 1px solid #879ba6;
    font-size: 13px;
    margin: 0;
    margin-right: 12px;
    height: 27px;
    font-family: Roboto-Regular, Roboto, sans-serif;
  }

  :host([appearance='secondary-toast'][disabled]:hover) {
    background: linear-gradient(90deg, rgb(116 230 74 / 50%) 0%, rgb(205 172 31 / 51%) 100%);
  }

  :host([appearance='secondary-toast']:not([disabled]):hover) {
    background: linear-gradient(90deg, #74e64a 0%, #cdac1f 100%);
    color: var(--black);
  }
`;

const appearanceBehavior = (value: string, styles: ElementStyles) => {
  return new PropertyStyleSheetBehavior('appearance', value, styles);
};

export const customButtonStyles = (
  context: ElementDefinitionContext,
  definition: FoundationElementDefinition
): ElementStyles =>
  css`
    ${buttonStyles(context, definition)}
    ${boxSizing}
    :host {
      margin: 0;
      transition: all 0.25s ease 0s;
    }

    :host::part(control) {
      height: auto;
    }

    :host(.with-icon)::part(control) {
      justify-content: flex-start;
    }

    :host .start {
      margin-inline-end: 7px;
    }

    :host(.return-button)::part(start) {
      margin-inline-end: 0;
    }

    :host(.return-button)::part(control) {
      width: auto !important;
      height: 28px;
      padding: 0 3px 0 9px;
    }

    :host .end {
      margin-inline-start: 7px;
      align-items: center;
      justify-content: center;
    }

    :host(zero-button.action-renderer[appearance='neutral']) {
      background-color: rgba(255, 255, 255, 0.05);
      min-width: auto;
      font-size: 13px;
      color: var(--font-color-1);
      margin-left: 10px;
    }

    :host(zero-button.action-renderer[appearance='neutral'])::part(control) {
      width: 26px;
      height: 25px;
    }
  `.withBehaviors(
    appearanceBehavior('default', defaultStyles),
    appearanceBehavior('stealth', stealthStyles),
    appearanceBehavior('primary', primaryStyles),
    appearanceBehavior('secondary', secondaryStyles),
    appearanceBehavior('tertiary', tertiaryStyles),
    appearanceBehavior('danger', dangerStyles),
    appearanceBehavior('lightweight', lightweightStyles),
    appearanceBehavior('tab-primary', tabPrimaryStyles),
    appearanceBehavior('tab-secondary', tabSecondaryStyles),
    appearanceBehavior('card-primary', cardPrimaryStyles),
    appearanceBehavior('card-secondary', cardSecondaryStyles),
    appearanceBehavior('chat-counter', chatCounterStyles),
    appearanceBehavior('chat-accept', chatAcceptStyles),
    appearanceBehavior('chat-reject', chatRejectStyles),
    appearanceBehavior('side-bar', sideBarStyles),
    appearanceBehavior('accent', accentStyles),
    appearanceBehavior('primary-toast', primaryToastStyles),
    appearanceBehavior('secondary-toast', secondaryToastStyles)
  );
